import React, { useState } from 'react';
import type {
  MrChannel,
  MrChannelIcon,
  MrKeywordMove,
  MrRevenuePoint,
  MrServiceRevenue,
  MrTone,
} from '../../growth-monthly-report/types';
import {
  MR_ACTIVITY_SECTIONS,
  MR_ALERTS,
  MR_AVAILABLE_MONTHS,
  MR_CHANNELS,
  MR_CURRENT_MONTH,
  MR_HERO,
  MR_KEYWORD_MOVEMENTS,
  MR_PATIENT_OUTCOMES,
  MR_REVENUE_TREND,
  MR_SEO_STATS,
  MR_TOTAL_ACTIONS,
} from '../../growth-monthly-report/mockData';
import {
  GROWTH_MONTHLY_REPORT_META,
  MR_MONTHS_OF_DATA,
} from '../../growth-monthly-report/config';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import './growth-monthly-report.css';

const TONE: Record<MrTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  lav: { accent: '#7A5CFF', tint: 'rgba(122,79,214,0.13)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const CHANNEL_ICON: Record<MrChannelIcon, IconKind> = {
  ads: 'clock',
  seo: 'search',
  gbp: 'pin',
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

function MrCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function MrSectionHeader({
  kicker,
  title,
  subtitle,
}: {
  kicker?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-3.5">
      {kicker ? (
        <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-[var(--success)]">
          {kicker}
        </div>
      ) : null}
      <h2 className="m-0 font-[var(--font-display)] text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-[var(--ink-0)]">
        {title}
      </h2>
      {subtitle ? (
        <p className="mt-1 text-[13.5px] leading-[1.5] text-[var(--ink-3)]">{subtitle}</p>
      ) : null}
    </div>
  );
}

function MrDelta({ change, suffix = '%' }: { change: number; suffix?: string }) {
  if (!change) return null;
  const positive = change > 0;
  const tone = positive ? TONE.success : TONE.terra;
  return (
    <span
      className="inline-flex items-center gap-[3px] rounded-[var(--r-pill)] px-[7px] py-[2px] font-[var(--font-mono)] text-[11px] font-bold"
      style={{ background: tone.tint, color: tone.accent }}
    >
      <Icon
        kind="up"
        size={9}
        stroke={3}
        style={{ transform: positive ? undefined : 'rotate(180deg)' }}
      />
      {positive ? '+' : ''}
      {change}
      {suffix}
    </span>
  );
}

function MrMonthSelector({
  months,
  selectedMonth,
  onMonthChange,
}: {
  months?: Array<{ label: string; value: string; status: 'month_to_date' | 'finalized' }>;
  selectedMonth?: string;
  onMonthChange?: (month: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const sourceMonths =
    months ??
    MR_AVAILABLE_MONTHS.map((month) => ({
      label: month.label,
      value: month.label,
      status: month.status,
    }));
  const activeMonth =
    sourceMonths.find((month) => month.value === selectedMonth) ?? sourceMonths[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((value) => !value);
        }}
        className="inline-flex h-10 cursor-pointer items-center gap-2.5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 text-[13px] font-medium text-[var(--ink-1)]"
      >
        <Icon kind="calendar" size={14} stroke={2} className="text-[var(--ink-3)]" />
        {activeMonth?.label ?? MR_CURRENT_MONTH.label}
        <Icon kind="down" size={12} stroke={2.5} className="text-[var(--ink-4)]" />
      </button>
      {open ? (
        <div className="elva-fade absolute right-0 top-[calc(100%+6px)] z-10 w-[240px] rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-1.5 shadow-[var(--sh-1)]">
          {sourceMonths.map((month) => {
            const active = month.value === activeMonth?.value;
            return (
              <button
                key={month.value}
                type="button"
                onClick={() => {
                  onMonthChange?.(month.value);
                  setOpen(false);
                }}
                className={[
                  'flex w-full cursor-pointer items-center justify-between rounded-[var(--r-sm)] px-3 py-2 text-left',
                  active ? 'bg-[var(--success-tint)]' : 'bg-transparent',
                ].join(' ')}
              >
                <span
                  className={[
                    'text-[13px]',
                    active
                      ? 'font-semibold text-[var(--success)]'
                      : 'font-normal text-[var(--ink-1)]',
                  ].join(' ')}
                >
                  {month.label}
                </span>
                {month.status === 'month_to_date' ? (
                  <span className="rounded-[var(--r-sm)] bg-[var(--gold-tint)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--gold)]">
                    LIVE
                  </span>
                ) : (
                  <span className="text-[10px] text-[var(--ink-4)]">Finalized</span>
                )}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function MrRevenueTrendChart({ points }: { points?: MrRevenuePoint[] }) {
  const sourcePoints = points ?? MR_REVENUE_TREND;
  if (sourcePoints.length < 2) return null;
  const chartHeight = 160;
  const chartWidth = 640;
  const barGap = 20;
  const barCount = sourcePoints.length;
  const barWidth = (chartWidth - barGap * (barCount - 1)) / barCount;
  const maxRevenue = Math.max(...sourcePoints.map((point) => point.revenue));

  return (
    <MrCard className="p-[22px_24px]">
      <div className="mb-[18px] flex items-baseline justify-between">
        <div>
          <div className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            Revenue Trend
          </div>
          <div className="mt-0.5 text-[12.5px] text-[var(--ink-3)]">
            Monthly attributed ad revenue · 6-month view
          </div>
        </div>
        <div className="text-[12px] text-[var(--ink-3)]">
          <span className="font-semibold text-[var(--success)]">+87%</span> vs. 6 months ago
        </div>
      </div>

      <svg
        width="100%"
        height={chartHeight + 30}
        viewBox={`0 0 ${String(chartWidth)} ${String(chartHeight + 30)}`}
        preserveAspectRatio="none"
      >
        {sourcePoints.map((point, index) => {
          const barHeight = (point.revenue / maxRevenue) * chartHeight;
          const x = index * (barWidth + barGap);
          const y = chartHeight - barHeight;
          return (
            <g key={point.month}>
              <text
                x={x + barWidth / 2}
                y={y - 8}
                textAnchor="middle"
                style={{
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                  fill: point.isCurrent ? 'var(--success)' : 'var(--ink-3)',
                  fontWeight: point.isCurrent ? 600 : 400,
                }}
              >
                ${(point.revenue / 1000).toFixed(0)}K
              </text>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx={6}
                fill={point.isCurrent ? 'var(--success)' : 'var(--success-tint)'}
              />
              <text
                x={x + barWidth / 2}
                y={chartHeight + 22}
                textAnchor="middle"
                style={{ fontSize: 11, fontFamily: 'var(--font-body)', fill: 'var(--ink-3)' }}
              >
                {point.month}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="mt-3.5 rounded-[var(--r-md)] bg-[var(--success-tint)] px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-[var(--ink-2)]">
        <strong className="text-[var(--success)]">Notable:</strong> April is trending toward your
        highest revenue month ever, driven by the Dental Implants campaign entering Exploitation
        phase (winner variant now at 100% traffic).
      </div>
    </MrCard>
  );
}

function MrRevenueByService({ services }: { services?: MrServiceRevenue[] }) {
  const sourceServices = services ?? MR_PATIENT_OUTCOMES.revenueByService;
  const total = sourceServices.reduce((sum: number, item: MrServiceRevenue) => sum + item.revenue, 0);
  if (sourceServices.length === 0) return null;
  return (
    <MrCard className="p-[22px_24px]">
      <div className="mb-4">
        <div className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          Revenue by Service
        </div>
        <div className="mt-0.5 text-[12.5px] text-[var(--ink-3)]">
          What services your new patients booked
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {sourceServices.map((item) => {
          const pct = (item.revenue / total) * 100;
          const tone = TONE[item.tone];
          return (
            <div key={item.service}>
              <div className="mb-[5px] flex items-baseline justify-between">
                <div className="flex items-center gap-2 text-[13px] text-[var(--ink-1)]">
                  <span
                    className="h-2 w-2 rounded-[2px]"
                    style={{ background: tone.accent }}
                  />
                  <span className="font-medium">{item.service}</span>
                  <span className="text-[11.5px] text-[var(--ink-3)]">
                    · {item.patients} patient{item.patients === 1 ? '' : 's'}
                  </span>
                </div>
                <span className="font-[var(--font-mono)] text-[13px] font-semibold text-[var(--ink-0)]">
                  ${item.revenue.toLocaleString()}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-[var(--surface-mute)]">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${String(pct)}%`, background: tone.accent }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </MrCard>
  );
}

function MrChannelCard({ channel }: { channel: MrChannel }) {
  const tone = TONE[channel.tone];
  return (
    <div
      className="rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[22px_24px] shadow-[var(--sh-1)]"
      style={{ borderLeft: `3px solid ${tone.accent}` }}
    >
      <div className="mb-4 flex items-center gap-3.5">
        <span
          className="inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[10px]"
          style={{ background: tone.tint, color: tone.accent }}
        >
          <Icon kind={CHANNEL_ICON[channel.icon]} size={20} stroke={2} />
        </span>
        <div className="flex-1">
          <div className="mb-0.5 font-[var(--font-display)] text-[18px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            {channel.name}
          </div>
          <div className="text-[12.5px] leading-[1.5] text-[var(--ink-3)]">{channel.summary}</div>
        </div>
      </div>

      <div
        className="mr-channel-grid grid gap-3 border-t border-[var(--cream-2)] pt-3.5"
        style={{
          gridTemplateColumns: `repeat(${String(channel.metrics.length)}, minmax(0, 1fr))`,
        }}
      >
        {channel.metrics.map((metric) => (
          <div key={metric.label}>
            <div className={`${eyebrowClass} mb-[3px]`}>{metric.label}</div>
            <div
              className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.01em]"
              style={{ color: metric.tone ? TONE[metric.tone].accent : 'var(--ink-0)' }}
            >
              {metric.value}
            </div>
          </div>
        ))}
      </div>

      {channel.note ? (
        <div className="mt-3.5 flex gap-2.5 rounded-[var(--r-md)] bg-[var(--surface-mute)] px-3.5 py-2.5">
          <Icon
            kind="info"
            size={14}
            stroke={2}
            className="mt-0.5 shrink-0 text-[var(--ink-3)]"
          />
          <span className="text-[12px] leading-[1.5] text-[var(--ink-2)]">{channel.note}</span>
        </div>
      ) : null}
    </div>
  );
}

function MrActivitySectionBlock({
  sections,
  totalActions,
}: {
  sections?: typeof MR_ACTIVITY_SECTIONS;
  totalActions?: string;
}) {
  const sourceSections = sections ?? MR_ACTIVITY_SECTIONS;
  const actionTotal = totalActions ?? MR_TOTAL_ACTIONS;
  return (
    <MrCard className="p-[24px_28px]">
      <div className="mb-5">
        <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-[var(--success)]">
          Autopilot evidence
        </div>
        <div className="font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          What ELVA Did This Month
        </div>
        <div className="mt-1 text-[13px] text-[var(--ink-3)]">
          Every action ELVA took while you focused on patients
        </div>
      </div>

      <div className="mr-activity-grid grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {sourceSections.map((section) => (
          <div
            key={section.id}
            className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--surface-mute)] p-[18px_20px]"
          >
            <div className="mb-3.5 text-[13px] font-semibold text-[var(--ink-0)]">
              {section.title}
            </div>
            <div className="flex flex-col gap-2.5">
              {section.items.map((item) => (
                <div key={item.label} className="flex items-baseline justify-between">
                  <span className="pr-2 text-[12px] leading-[1.3] text-[var(--ink-2)]">
                    {item.label}
                  </span>
                  <span className="shrink-0 font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--success)]">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-[18px] flex items-center gap-2.5 rounded-[var(--r-md)] bg-[var(--success-tint)] px-4 py-3">
        <Icon kind="bolt" size={16} stroke={2} className="shrink-0 text-[var(--success)]" />
        <div className="text-[12.5px] leading-[1.5] text-[var(--ink-2)]">
          <strong className="text-[var(--success)]">{actionTotal} total autopilot actions</strong>{' '}
          this month — from bid adjustments to content generation, executed automatically based on
          real-time data.{' '}
          <span className="cursor-pointer font-semibold text-[var(--success)] underline underline-offset-2">
            View full log →
          </span>
        </div>
      </div>
    </MrCard>
  );
}

function MrKeywordMovements({ movements }: { movements?: MrKeywordMove[] }) {
  const sourceMovements = movements ?? MR_KEYWORD_MOVEMENTS;
  if (sourceMovements.length === 0) return null;
  return (
    <MrCard className="p-[22px_24px]">
      <div className="mb-4">
        <div className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          Keyword Ranking Changes
        </div>
        <div className="mt-0.5 text-[12.5px] text-[var(--ink-3)]">
          Top movements in Google search positions this month
        </div>
      </div>
      <div className="flex flex-col">
        {sourceMovements.map((item: MrKeywordMove, index) => {
          const positive = item.change > 0;
          const tone = positive ? TONE.success : TONE.terra;
          return (
            <div
              key={item.keyword}
              className="flex items-center justify-between py-3"
              style={{
                borderBottom:
                  index < sourceMovements.length - 1
                    ? '1px solid var(--cream-2)'
                    : undefined,
              }}
            >
              <div className="text-[13px] font-medium text-[var(--ink-1)]">{item.keyword}</div>
              <div className="flex items-center gap-4 font-[var(--font-mono)]">
                <span className="text-[12px] text-[var(--ink-4)]">#{item.from}</span>
                <Icon kind="arrow-r" size={14} stroke={2} className="text-[var(--ink-4)]" />
                <span className="text-[13px] font-semibold text-[var(--ink-0)]">#{item.to}</span>
                <span
                  className="inline-flex min-w-[50px] items-center justify-center gap-[3px] rounded-[var(--r-pill)] px-[7px] py-[2px] text-[11px] font-bold"
                  style={{ background: tone.tint, color: tone.accent }}
                >
                  <Icon
                    kind="up"
                    size={9}
                    stroke={3}
                    style={{ transform: positive ? undefined : 'rotate(180deg)' }}
                  />
                  {positive ? '+' : ''}
                  {item.change}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </MrCard>
  );
}

export interface MonthlyReportData {
  hero?: typeof MR_HERO;
  patientOutcomes?: typeof MR_PATIENT_OUTCOMES;
  currentMonth?: typeof MR_CURRENT_MONTH;
  alerts?: typeof MR_ALERTS;
  channels?: typeof MR_CHANNELS;
  seoStats?: typeof MR_SEO_STATS;
  activitySections?: typeof MR_ACTIVITY_SECTIONS;
  keywordMovements?: typeof MR_KEYWORD_MOVEMENTS;
  revenueTrend?: typeof MR_REVENUE_TREND;
  totalActions?: string;
}

export interface MonthlyReportViewProps {
  data?: MonthlyReportData;
  availableMonths?: Array<{ label: string; value: string; status: 'month_to_date' | 'finalized' }>;
  selectedMonth?: string;
  onMonthChange?: (month: string) => void;
  generating?: boolean;
  loading?: boolean;
  onExport?: () => void;
  exportPending?: boolean;
}

export function MonthlyReportView({
  data,
  availableMonths,
  selectedMonth,
  onMonthChange,
  generating = false,
  loading = false,
  onExport,
  exportPending = false,
}: MonthlyReportViewProps = {}) {
  const meta = GROWTH_MONTHLY_REPORT_META;
  const hero = data?.hero ?? MR_HERO;
  const outcomes = data?.patientOutcomes ?? MR_PATIENT_OUTCOMES;

  const currentMonth = data?.currentMonth ?? MR_CURRENT_MONTH;
  const alerts = data?.alerts ?? MR_ALERTS;
  const channels = data?.channels ?? MR_CHANNELS;
  const seoStats = data?.seoStats ?? MR_SEO_STATS;
  const activitySections = data?.activitySections ?? MR_ACTIVITY_SECTIONS;
  const revenueTrend = data?.revenueTrend ?? MR_REVENUE_TREND;
  const keywordMovements = data?.keywordMovements ?? MR_KEYWORD_MOVEMENTS;
  const totalActions = data?.totalActions ?? MR_TOTAL_ACTIONS;
  const showRevenueTrend = revenueTrend.length >= 6 || MR_MONTHS_OF_DATA >= 6;

  const roiDelta = Math.round(((hero.roi - hero.lastMonth.roi) / hero.lastMonth.roi) * 100);
  const revenueDelta = Math.round(
    ((hero.revenue - hero.lastMonth.revenue) / hero.lastMonth.revenue) * 100,
  );
  const patientsDelta = hero.newPatients - hero.lastMonth.newPatients;

  return (
    <div className="growth-monthly-report-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="doc" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
            <p className="m-0 text-[14px] text-[var(--ink-2)]">
              {currentMonth.label}
              {loading ? ' · Updating…' : ''}
            </p>
            {currentMonth.status === 'month_to_date' ? (
              <span className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] bg-[var(--gold-tint)] px-2.5 py-1 text-[11px] font-semibold text-[var(--gold)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]" />
                Month-to-date · Day {currentMonth.daysElapsed} of {currentMonth.daysTotal}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex gap-2.5">
          <MrMonthSelector
            {...(availableMonths ? { months: availableMonths } : {})}
            {...(selectedMonth ? { selectedMonth } : {})}
            {...(onMonthChange ? { onMonthChange } : {})}
          />
          <button
            type="button"
            disabled={!onExport || exportPending}
            onClick={onExport}
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-[var(--r-md)] px-4 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            style={{ background: 'var(--grad-ink)' }}
          >
            <Icon kind="download" size={14} stroke={2} />
            {exportPending ? 'Exporting…' : 'Export PDF'}
          </button>
        </div>
      </div>

      {generating ? (
        <MrCard className="mb-6 border-[color-mix(in_srgb,var(--gold)_30%,transparent)] bg-[var(--gold-tint)] px-5 py-4">
          <div className="flex items-center gap-3 text-[13.5px] text-[var(--ink-2)]">
            <Icon kind="clock" size={18} stroke={2} className="shrink-0 text-[var(--gold)]" />
            <span>
              Report is still generating. Channel metrics update as data arrives — richer sections
              use reference data until the full payload is ready.
            </span>
          </div>
        </MrCard>
      ) : null}

      {/* HERO */}
      <MrCard className="mb-6 border-[color-mix(in_srgb,var(--success)_26%,transparent)] bg-[var(--success-tint)] p-[36px_40px]">
        <div className="mb-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--success)]">
          The Headline
        </div>
        <div className="mb-2 max-w-[820px] font-[var(--font-display)] text-[30px] font-semibold leading-[1.3] tracking-[-0.03em] text-[var(--ink-0)]">
          You invested{' '}
          <span className="font-bold text-[var(--ink-0)]">${hero.adSpend.toLocaleString()}</span> in
          Google Ads this month. That investment returned{' '}
          <span className="font-bold text-[var(--success)]">${hero.revenue.toLocaleString()}</span>{' '}
          in booked patient revenue — a{' '}
          <span className="font-bold text-[var(--success)]">{hero.roi}x return</span>.
        </div>
        <p className="m-0 mb-7 max-w-[760px] text-[14.5px] leading-[1.6] text-[var(--ink-2)]">
          For context, the average dental practice sees 4-6x return on marketing spend. You&apos;re
          running well above that — driven by your Dental Implants campaign (37x ROI) and a strong
          Invisalign pipeline. SEO and GBP are building long-term visibility that supports this
          pipeline.
        </p>

        <div className="mr-hero-grid grid grid-cols-1 gap-5 border-t border-[color-mix(in_srgb,var(--success)_22%,transparent)] pt-6 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <div className={`${eyebrowClass} mb-1.5`}>Ad Spend</div>
            <div className="font-[var(--font-display)] text-[30px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--ink-0)]">
              ${hero.adSpend.toLocaleString()}
            </div>
            <div className="mt-1.5">
              <MrDelta change={10} />{' '}
              <span className="ml-1 text-[11.5px] text-[var(--ink-3)]">vs. last month</span>
            </div>
          </div>
          <div>
            <div className={`${eyebrowClass} mb-1.5`}>Revenue Generated</div>
            <div className="font-[var(--font-display)] text-[30px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--success)]">
              ${(hero.revenue / 1000).toFixed(1)}K
            </div>
            <div className="mt-1.5">
              <MrDelta change={revenueDelta} />
              <span className="ml-1 text-[11.5px] text-[var(--ink-3)]">vs. last month</span>
            </div>
          </div>
          <div>
            <div className={`${eyebrowClass} mb-1.5`}>Return on $1 Spent</div>
            <div className="font-[var(--font-display)] text-[30px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--success)]">
              {hero.roi}x
            </div>
            <div className="mt-1.5">
              <MrDelta change={roiDelta} />
              <span className="ml-1 text-[11.5px] text-[var(--ink-3)]">vs. last month</span>
            </div>
          </div>
          <div>
            <div className={`${eyebrowClass} mb-1.5`}>New Patients Booked</div>
            <div className="font-[var(--font-display)] text-[30px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--ink-0)]">
              {hero.newPatients}
            </div>
            <div className="mt-1.5">
              <MrDelta change={patientsDelta} suffix="" />
              <span className="ml-1 text-[11.5px] text-[var(--ink-3)]">
                vs. {hero.lastMonth.newPatients} last month
              </span>
            </div>
          </div>
        </div>
      </MrCard>

      {/* ALERTS */}
      {alerts.length > 0 ? (
        <div className="mb-6">
          {alerts.map((alert) => (
            <div
              key={alert.title}
              className="flex gap-3.5 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--gold)_30%,transparent)] bg-[var(--gold-tint)] px-[18px] py-3.5"
            >
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--r-sm)] bg-[var(--cream-1)] text-[var(--gold)]">
                <Icon kind="alert" size={16} stroke={2.2} />
              </span>
              <div className="flex-1">
                <div className="mb-0.5 text-[13.5px] font-semibold text-[var(--gold)]">
                  {alert.title}
                </div>
                <div className="text-[12.5px] leading-[1.6] text-[var(--ink-2)]">
                  {alert.detail}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* SECTION 1: Patient Outcomes */}
      <MrSectionHeader
        kicker="Section 1"
        title="Patient Outcomes"
        subtitle="Real booked patients and revenue from Google Ads this month"
      />

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <MrCard className="p-[20px_22px]">
          <div className={`${eyebrowClass} mb-1.5`}>New Patients</div>
          <div className="flex items-baseline gap-2">
            <span className="font-[var(--font-display)] text-[28px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--success)]">
              {outcomes.newPatients.value}
            </span>
            <MrDelta change={outcomes.newPatients.change} />
          </div>
          <div className="mt-1.5 text-[12px] text-[var(--ink-3)]">
            vs. {outcomes.newPatients.lastMonth} last month
          </div>
        </MrCard>

        <MrCard className="p-[20px_22px]">
          <div className={`${eyebrowClass} mb-1.5`}>Total Appointments</div>
          <div className="flex items-baseline gap-2">
            <span className="font-[var(--font-display)] text-[28px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--ink-0)]">
              {outcomes.totalAppointments.value}
            </span>
            <MrDelta change={outcomes.totalAppointments.change} />
          </div>
          <div className="mt-1.5 text-[12px] text-[var(--ink-3)]">
            Ad-attributed bookings this month
          </div>
        </MrCard>

        <MrCard className="p-[20px_22px]">
          <div className={`${eyebrowClass} mb-1.5`}>Est. 3-Year Revenue</div>
          <div className="flex items-baseline gap-2">
            <span className="font-[var(--font-display)] text-[28px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--success)]">
              ${(outcomes.projectedLTV.value / 1000).toFixed(0)}K
            </span>
            <MrDelta change={outcomes.projectedLTV.change} />
          </div>
          <div className="mt-1.5 text-[12px] text-[var(--ink-3)]">
            Projected from new patients based on service mix
          </div>
        </MrCard>
      </div>

      {MR_MONTHS_OF_DATA >= 6 || showRevenueTrend ? (
        <div className="mr-revenue-grid mb-9 grid gap-4">
          <MrRevenueTrendChart points={revenueTrend} />
          <MrRevenueByService services={outcomes.revenueByService} />
        </div>
      ) : (
        <div className="mb-9">
          <MrRevenueByService services={outcomes.revenueByService} />
        </div>
      )}

      {/* SECTION 2: Channel Performance */}
      <MrSectionHeader
        kicker="Section 2"
        title="Channel Performance"
        subtitle="Where your patients come from and where ELVA is building visibility"
      />

      <div className="mb-9 flex flex-col gap-3">
        {channels.map((channel) => (
          <MrChannelCard key={channel.id} channel={channel} />
        ))}
      </div>

      {/* SECTION 3: What ELVA Did */}
      <div className="mb-9">
        <MrActivitySectionBlock sections={activitySections} totalActions={totalActions} />
      </div>

      {/* SECTION 4: Content & SEO Progress */}
      <MrSectionHeader
        kicker="Section 4"
        title="Content & SEO Progress"
        subtitle="Long-term organic assets ELVA is building for you"
      />

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {seoStats.map((stat) => (
          <MrCard key={stat.key} className="p-[20px_22px]">
            <div className={`${eyebrowClass} mb-1.5`}>{stat.label}</div>
            <div className="flex items-baseline gap-2">
              <span
                className="font-[var(--font-display)] text-[28px] font-semibold leading-[1.1] tracking-[-0.03em]"
                style={{ color: stat.key === 'articles' ? 'var(--success)' : 'var(--ink-0)' }}
              >
                {stat.value}
              </span>
              {typeof stat.change === 'number' ? (
                <MrDelta change={stat.change} suffix={stat.suffix ?? '%'} />
              ) : null}
            </div>
            <div className="mt-1.5 text-[12px] text-[var(--ink-3)]">{stat.note}</div>
          </MrCard>
        ))}
      </div>

      <div className="mb-12">
        <MrKeywordMovements movements={keywordMovements} />
      </div>

      {/* FOOTER */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-6 text-[12px] text-[var(--ink-4)]">
        <div>Report generated by ELVA · Data verified via PMS integration · Updated every hour</div>
      </div>
    </div>
  );
}
